/**
 * Only same-origin relative paths may be used as a post-auth destination —
 * `?next=` is attacker-writable, so absolute URLs, protocol-relative `//host`
 * forms, backslash variants, and tab/newline forms (browsers strip those into
 * `//host`) would be an open redirect.
 */
export function safeNextPath(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/')) return '/'
  if (raw.startsWith('//') || /[\\\t\n\r]/.test(raw)) return '/'
  return raw
}

/** Parameters Better Auth adds when it signs the authorize request it bounces to /sign-in. */
const SIGNED_QUERY_PARAMS = ['sig', 'exp', 'ba_iat', 'ba_param']

/**
 * When `/oauth2/authorize` bounced a signed-out visitor here, the way back into
 * that authorization (which then shows the consent screen): the original request
 * re-sent to the authorize endpoint, minus Better Auth's signing parameters. The
 * endpoint re-validates the client, redirect URI and PKCE itself, so this is no
 * wider than visiting it directly. Null for any other sign-in.
 */
export function oauthResumePath(search: string): string | null {
  const params = new URLSearchParams(search)
  if (!params.has('sig') || !params.has('response_type') || !params.has('client_id')) return null
  for (const name of [...SIGNED_QUERY_PARAMS, 'next']) params.delete(name)
  return `/api/auth/oauth2/authorize?${params.toString()}`
}

/** Where the current page sends a user once signed in: a pending OAuth authorization, else its `?next=`, made safe. */
export function currentNextPath(): string {
  if (typeof window === 'undefined') return '/'
  const search = window.location.search
  return oauthResumePath(search) ?? safeNextPath(new URLSearchParams(search).get('next'))
}
